import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, Compass, BookOpen, Briefcase, Brain, Layers, Users, Lightbulb, CheckCircle2, ChevronDown,
} from "lucide-react";
import { pillars } from "@/data";

const iconMap: Record<string, React.ElementType> = {
  Target, Compass, BookOpen, Briefcase, Brain, Layers, Users, Lightbulb,
};

export default function Pillars() {
  const [active, setActive] = useState<string | null>("obe");

  return (
    <div className="py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Core Architecture</p>
          <h1 className="text-4xl font-bold text-foreground mb-3">8 Core Pedagogical Pillars</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Each pillar represents a distinct and integrated dimension of GBSRC's world-class learning philosophy, inspired by global best practices and grounded in OBE principles.
          </p>
        </div>

        {/* Grid of pillar cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {pillars.map((p, i) => {
            const Icon = iconMap[p.icon] ?? Target;
            const isActive = active === p.id;
            return (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                onClick={() => setActive(isActive ? null : p.id)}
                className={`text-left rounded-xl p-4 border transition-all duration-200 ${
                  isActive
                    ? "border-primary bg-primary text-primary-foreground shadow-lg"
                    : "border-border bg-background hover:border-primary/50 hover:bg-muted/40"
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${
                  isActive ? "bg-white/20" : `bg-gradient-to-br ${p.color}`
                }`}>
                  <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-white"}`} />
                </div>
                <p className={`text-[10px] font-semibold mb-0.5 ${isActive ? "text-white/70" : "text-muted-foreground"}`}>
                  {p.subtitle}
                </p>
                <p className={`text-sm font-bold leading-tight ${isActive ? "text-white" : "text-foreground"}`}>
                  {p.title}
                </p>
                <ChevronDown className={`w-4 h-4 mt-2 transition-transform ${isActive ? "rotate-180 text-white/70" : "text-muted-foreground"}`} />
              </motion.button>
            );
          })}
        </div>

        {/* Detail panel */}
        <AnimatePresence mode="wait">
          {active && (() => {
            const p = pillars.find((x) => x.id === active)!;
            const Icon = iconMap[p.icon] ?? Target;
            return (
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="bg-background border border-primary/30 rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-start gap-5">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center shrink-0`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest mb-1">{p.subtitle}</p>
                    <h2 className="text-2xl font-bold text-foreground mb-3">{p.title}</h2>
                    <p className="text-foreground/70 leading-relaxed mb-5">{p.description}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {p.points.map((pt) => (
                        <div key={pt} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground/80">{pt}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </AnimatePresence>

        {/* All pillars quick view */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-foreground mb-6">All Pillars at a Glance</h2>
          <div className="space-y-3">
            {pillars.map((p, i) => {
              const Icon = iconMap[p.icon] ?? Target;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4 bg-muted/30 border border-border rounded-xl p-4"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-foreground">{p.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">{p.description.slice(0, 100)}…</p>
                  </div>
                  <span className="text-xs font-semibold text-primary border border-primary/30 px-2 py-0.5 rounded">
                    Pillar {i + 1}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
